import { ANAESTHETIC_PRIORITIES, EDT_ACTIVITY_OPTIONS, LEARNER_GRADES, MAX_EDT_DURATION_MINUTES, labelFor, } from './enums.js';
import { DEFAULT_IMPORT_HOUR, normalise, parseAgeBand, parseCareLevel, parseImportedDate, parseSupervision, procedureKeyFor, } from './import.js';
import { procedureByKey } from './procedures.js';
/**
 * Recognising a sheet from one of the two logbooks trainees arrive with, and
 * turning it into this app's records.
 *
 * `docs/IMPORT_SOURCES.md` is the specification and decides every mapping here.
 * If the two disagree, the document is right and this is the bug.
 *
 * Why profiles rather than one generic column-mapper: the Excel ICU logbook's
 * `ICU patients` sheet is 49 columns wide and one row is an admission plus up
 * to a dozen procedures. No amount of "which column is the date" gets you
 * there, and asking a trainee to map 49 columns by hand is not an import, it is
 * a punishment. A recognised sheet needs no mapping at all.
 *
 * Nothing here writes anything. Every function returns a plan the user confirms.
 */
/* ------------------------------------------------------------ refusals */
/**
 * Columns this app refuses to import, whatever the user asks.
 *
 * Refused in code rather than by convention, and reported to the user by name
 * so the absence is never a silent one. A date of birth is the sharp one: it
 * sits in the Excel logbook that a new user will hand us on their first day,
 * and rule 1 in CLAUDE.md admits no exception.
 */
export const REFUSED_COLUMNS = [
    { header: 'date of birth', why: 'A date of birth identifies a patient. The age band is used instead.' },
    { header: 'dob', why: 'A date of birth identifies a patient. The age band is used instead.' },
    { header: 'patient', why: 'This app has no field for anything identifying a patient.' },
    { header: 'personal reference', why: 'Free text that often identifies a patient.' },
    { header: 'reference', why: 'Free text that often identifies a patient.' },
    { header: 'supervisor', why: 'Another person’s name. Not your data to import.' },
    { header: 'procedure supervisor', why: 'Another person’s name. Not your data to import.' },
    { header: 'tutor', why: 'Another person’s name. Not your data to import.' },
    { header: 'tutor 2', why: 'Another person’s name. Not your data to import.' },
    { header: 'sex', why: 'A patient attribute this app does not collect.' },
    { header: 'hospital', why: 'Not recorded by this app, deliberately.' },
    { header: 'deanery', why: 'Not recorded by this app, deliberately.' },
    { header: 'school', why: 'Not recorded by this app, deliberately.' },
];
/** Which of a sheet's headers this app will refuse, with the reason for each. */
export function refusedColumns(headers) {
    const found = [];
    for (const header of headers) {
        const match = REFUSED_COLUMNS.find((rule) => normalise(header) === rule.header);
        if (match !== undefined)
            found.push({ header, why: match.why });
    }
    return found;
}
export const IMPORT_PROFILES = [
    {
        key: 'llp_intensive',
        label: 'Intensive care cases',
        source: 'Anaesthetics Lifelong Learning Platform',
        signature: ['Event', 'Supervision', 'Age'],
        produces: ['case', 'procedure'],
        description: 'Each row becomes a case. Rows recording an intra-hospital transfer also gain a transfer procedure inside the case.',
    },
    {
        key: 'llp_procedures',
        label: 'Stand-alone procedures',
        source: 'Anaesthetics Lifelong Learning Platform',
        signature: ['Specialty', 'Procedure Type (Medicine)', 'Supervision'],
        produces: ['procedure'],
        description: 'Each row becomes a procedure. This sheet has no age column, so every row takes the age band you choose below.',
    },
    {
        key: 'llp_anaesthetic',
        label: 'Anaesthetic cases',
        source: 'Anaesthetics Lifelong Learning Platform',
        signature: ['Operation', 'Mode of Anaesthesia', 'ASA'],
        produces: ['anaesthetic', 'procedure'],
        description: 'Each row becomes an anaesthetic case, counted separately from your intensive care work. Rows that also record a procedure gain one.',
    },
    {
        key: 'llp_acute_pain',
        label: 'Acute pain reviews',
        source: 'Anaesthetics Lifelong Learning Platform',
        signature: ['Pain Type', 'Review Type', 'Review Nature'],
        produces: ['anaesthetic'],
        description: 'Each row becomes an anaesthetic case recorded as acute pain, counted separately from your intensive care work.',
    },
    {
        key: 'llp_session',
        label: 'Sessions',
        source: 'Anaesthetics Lifelong Learning Platform',
        signature: ['Activity', 'Supervision', 'Date'],
        produces: ['edt'],
        description: 'Each row becomes an EDT / Teaching entry.',
    },
    {
        key: 'excel_icu_patients',
        label: 'ICU patients',
        source: 'Excel ICU Logbook',
        signature: ['Arterial line inserted', 'CVC inserted', 'Procedure 1'],
        produces: ['case', 'procedure'],
        description: 'Each row becomes one case with its procedures inside it — the arterial line, the central line, the vascath, the ultrasounds and anything in the four procedure columns.',
    },
    {
        key: 'excel_ward_review',
        label: 'Ward reviews',
        source: 'Excel ICU Logbook',
        signature: ['Reason for consultation', 'Reason for non-admission'],
        produces: ['case'],
        description: 'Each row becomes a ward review case.',
    },
    {
        key: 'excel_transfers',
        label: 'Transfers',
        source: 'Excel ICU Logbook',
        signature: ['Intra/Extra hospital', 'Transport'],
        produces: ['procedure'],
        description: 'Each row becomes a transfer procedure. A transfer is a procedure in this app, and there is no case type that fits one.',
    },
    {
        key: 'imt_teaching_attended',
        label: 'Teaching Record',
        source: 'IMT Logbook',
        signature: ['Teaching Type', 'Topic', 'Tutor'],
        produces: ['edt'],
        description: 'Each row becomes an EDT / Teaching entry recording teaching you attended.',
    },
    {
        key: 'imt_teaching_delivered',
        label: 'Teaching Delivered',
        source: 'IMT Logbook',
        signature: ['Teaching Type', 'Topic', 'Audience'],
        produces: ['edt'],
        description: 'Each row becomes an EDT / Teaching entry recording teaching you delivered.',
    },
    {
        key: 'imt_clinic',
        label: 'Clinic Record',
        source: 'IMT Logbook',
        signature: ['Clinic Date', 'Clinic Type', 'Diagnosis'],
        produces: ['edt'],
        description: 'Each row becomes an EDT / Teaching entry recording a clinic attended.',
    },
    {
        key: 'imt_procedures',
        label: 'Procedures',
        source: 'IMT Logbook',
        signature: ['Procedure', 'Skills lab', 'Supervised practice', 'DOPS completed'],
        produces: ['procedure'],
        description: 'Each row becomes a procedure. This sheet has no age band or capability level column, so every row takes the age band and capability level you choose below.',
    },
];
/**
 * Which logbook and sheet this is, from its headers alone.
 *
 * Every signature header must be present. Headers are compared normalised, so
 * capitalisation and punctuation differences between exports do not matter.
 * Returns null for anything unrecognised, which falls back to the manual
 * column mapper that has always been there.
 */
export function detectProfile(headers) {
    const present = new Set(headers.map(normalise));
    return (IMPORT_PROFILES.find((profile) => profile.signature.every((header) => present.has(normalise(header)))) ?? null);
}
/**
 * Whether an undated row is worth telling the user about.
 *
 * The Excel logbook ships with twenty numbered rows carrying nothing but a row
 * number and the dropdown defaults. Reporting those as twenty skipped entries
 * buries the one row that actually failed. A row is only worth reporting if the
 * trainee wrote something into it that this app would have used.
 */
function hasContent(row, headers) {
    // `No` and `0` are what an untouched dropdown says, not something written.
    const EMPTY = ['', 'no', '0', 'none'];
    return headers.some((header) => !EMPTY.includes(normalise(get(row, header))));
}
function get(row, header) {
    const key = Object.keys(row).find((name) => normalise(name) === normalise(header));
    return key === undefined ? '' : (row[key] ?? '').trim();
}
function has(row, header) {
    return get(row, header) !== '';
}
/** Yes/no columns in both logbooks. Anything else is "not recorded". */
function isYes(row, header) {
    return ['yes', 'y', 'true'].includes(normalise(get(row, header)));
}
function when(row, settings, dateHeader = 'Date', timeHeader) {
    const raw = get(row, dateHeader);
    if (raw === '')
        return null;
    const time = timeHeader === undefined ? '' : get(row, timeHeader);
    const parsed = parseImportedDate(raw, settings.dateFormat, time === '' ? undefined : time);
    if (parsed === null)
        return null;
    // No usable time at all: midday, the least-claiming option. Anything in the
    // small hours would inflate the out-of-hours percentage on a missing cell.
    if (time === '' && parsed.getHours() === 0 && parsed.getMinutes() === 0) {
        parsed.setHours(DEFAULT_IMPORT_HOUR, 0, 0, 0);
    }
    return parsed;
}
function level(row, settings, header = 'Supervision') {
    return parseSupervision(get(row, header)) ?? settings.defaultCapabilityLevel;
}
function age(row, settings, header = 'Age') {
    return parseAgeBand(get(row, header)) ?? settings.defaultAgeBand;
}
/**
 * Columns with no field of their own, folded into the note.
 *
 * Dropping a trainee's own record of their case would be worse than a slightly
 * long note, and these are the columns the two logbooks carry that this app
 * models nothing for — level of care, organ failure counts, transport mode.
 */
function extraNote(row, headers) {
    return headers
        .map((header) => (has(row, header) ? `${header}: ${get(row, header)}` : ''))
        .filter((part) => part !== '')
        .join(' · ');
}
function withNote(row, extras, noteHeader = 'Notes') {
    const parts = [get(row, noteHeader), extraNote(row, extras)].filter((part) => part !== '');
    const note = parts.join(' · ').slice(0, 500);
    return note === '' ? undefined : note;
}
/** The grades either logbook records against a teaching column. */
function teaching(row, header = 'Teaching') {
    const raw = normalise(get(row, header));
    if (raw === '' || raw === 'none')
        return undefined;
    const map = {
        'medical student': 'medical_student',
        'medical students': 'medical_student',
        nurse: 'nurse',
        nurses: 'nurse',
        'allied health professional': 'ahp',
        'foundation trainee': 'foundation_doctor',
        'foundation trainees': 'foundation_doctor',
        novice: 'core_trainee',
        'ct 1 2': 'core_trainee',
        'ct1 2': 'core_trainee',
        trainee: 'higher_trainee',
        'st 3 4': 'higher_trainee',
        'st3 4': 'higher_trainee',
        'st 5 7': 'higher_trainee',
        'st5 7': 'higher_trainee',
        'non trainee': 'sas_or_non_trainee',
        consultant: 'consultant',
        'consultants': 'consultant',
        'consultant career grade': 'consultant',
    };
    const grade = map[raw];
    if (grade !== undefined)
        return [grade];
    return LEARNER_GRADES.includes(raw) ? [raw] : ['other'];
}
export const EMPTY_PLAN = {
    cases: [],
    procedures: [],
    edtEntries: [],
    anaestheticCases: [],
    skipped: [],
    refused: [],
};
/* ------------------------------------------------------------- procedures */
/**
 * The catalogue's own spelling of a variant, matched case-insensitively.
 *
 * Returns undefined for anything the procedure does not offer — the Excel
 * logbook has a `Dorsalis Pedis` arterial line and an `Other`, and the schema
 * rejects a variant that is not in the catalogue's list. Losing the word is
 * better than inventing a variant to keep it.
 */
function variantFor(procedureKey, value) {
    const raw = normalise(value);
    if (raw === '' || raw === 'no' || raw === 'other')
        return undefined;
    const variants = procedureByKey(procedureKey)?.variants ?? [];
    const exact = variants.find((variant) => normalise(variant) === raw);
    if (exact !== undefined)
        return exact;
    /*
     * The platform names the site inside the procedure itself —
     * `central-venous-access–internal-jugular`, `Percutaneous Tracheostomy` — so
     * an exact match alone loses the site on every central line in the export.
     * Longest first, so a variant that contains another cannot be shadowed by it.
     */
    return [...variants]
        .sort((a, b) => b.length - a.length)
        .find((variant) => raw.includes(normalise(variant)));
}
function procedure(procedureKey, occurredAt, ageBand, capabilityLevel, extras = {}) {
    return { procedureKey, occurredAt, ageBand, capabilityLevel, ...extras };
}
/**
 * The Excel logbook's `Ultrasound 1` and `Ultrasound 2` columns.
 *
 * **Column-scoped, and it has to be.** `Pleural` here is a lung scan; the same
 * word in a procedure column is `Pleural Tap`, an aspiration — which is what
 * matching by name resolves it to, correctly, because that is far more commonly
 * logged. Nothing in the string distinguishes them, so the column wins.
 */
const EXCEL_ULTRASOUND = {
    fice: 'transthoracic_echo',
    pleural: 'lung_ultrasound',
    abdo: 'ultrasound_abdomen',
    fast: 'ultrasound_abdomen',
    aaa: 'ultrasound_abdomen',
    renal: 'ultrasound_abdomen',
    hepbil: 'ultrasound_abdomen',
    bladder: 'ultrasound_abdomen',
    dvt: 'ultrasound_dvt',
    // No equivalent in the ICM catalogue.
    ocular: null,
    other: null,
};
/* ---------------------------------------------------------- the planners */
/** Anaesthetics platform → intensive care cases. */
function planLlpIntensive(rows, settings) {
    const plan = { ...EMPTY_PLAN, cases: [], skipped: [] };
    rows.forEach((row, index) => {
        const occurredAt = when(row, settings, 'Date', 'Time');
        if (occurredAt === null) {
            plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Date') });
            return;
        }
        const events = get(row, 'Event')
            .split(',')
            .map((part) => normalise(part))
            .filter((part) => part !== '');
        // Both present means one episode described two ways. Admission is the
        // larger event and wins; the row is not two cases.
        const caseType = events.includes('admission')
            ? 'admission'
            : events.includes('ward review')
                ? 'ward_review'
                : 'admission';
        const reasons = [];
        if (events.includes('cardiac arrest'))
            reasons.push('cardiac_arrest');
        const hillos = events.includes('discussion with relatives') ? [8] : undefined;
        const ageBand = age(row, settings);
        const capabilityLevel = level(row, settings);
        const label = caseType === 'admission' ? 'Admission' : 'Ward review';
        const input = {
            caseType,
            // This sheet records no diagnosis, and a title is required. The case type
            // and the date is honest; inventing a diagnosis would not be.
            title: `${label} — ${get(row, 'Date')}`,
            occurredAt,
            ageBand,
            capabilityLevel,
            ...(reasons.length === 0 ? {} : { reasonsForReferral: reasons }),
            ...(hillos === undefined ? {} : { hillos: [...hillos] }),
            ...(withNote(row, []) === undefined ? {} : { note: withNote(row, []) }),
        };
        const nested = events.includes('intra hospital transfer')
            ? [procedure('transfer_intra_hospital', occurredAt, ageBand, capabilityLevel)]
            : [];
        plan.cases.push({ input, procedures: nested });
    });
    return plan;
}
/** Anaesthetics platform → stand-alone procedures. */
function planLlpProcedures(rows, settings) {
    const plan = { ...EMPTY_PLAN, procedures: [], skipped: [] };
    rows.forEach((row, index) => {
        const occurredAt = when(row, settings, 'Date');
        if (occurredAt === null) {
            plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Date') });
            return;
        }
        // One of the three specialty columns is filled per row.
        const raw = get(row, 'Procedure Type (Medicine)') ||
            get(row, 'Procedure Type (Anaesthesia)') ||
            get(row, 'Procedure Type (Pain)');
        if (raw === '') {
            plan.skipped.push({ index, reason: 'No procedure named', value: '' });
            return;
        }
        const key = procedureKeyFor(raw);
        if (key === null) {
            plan.skipped.push({ index, reason: 'No matching procedure in this app', value: raw });
            return;
        }
        plan.procedures.push(procedure(key, occurredAt, age(row, settings), level(row, settings), {
            ...(variantFor(key, raw) === undefined ? {} : { variant: variantFor(key, raw) }),
            ...(withNote(row, ['Specialty']) === undefined
                ? {}
                : { note: withNote(row, ['Specialty']) }),
        }));
    });
    return plan;
}
/**
 * Anaesthetics platform → anaesthetic cases, and the procedures inside them.
 *
 * The platform writes a case's *second* procedure as a continuation row: every
 * case column blank — no Case ID, no date, no operation — and only the
 * procedure and its supervision filled in. In the sample export 41 rows are
 * continuations and 31 of them carry a real procedure, so reading them as
 * undated rows and skipping them loses 31 procedures a trainee actually did,
 * silently. They inherit the date and age band of the case above.
 */
function planLlpAnaesthetic(rows, settings) {
    const plan = {
        ...EMPTY_PLAN,
        anaestheticCases: [],
        procedures: [],
        skipped: [],
    };
    /** The case the continuation rows below belong to. */
    let parent = null;
    rows.forEach((row, index) => {
        const isContinuation = get(row, 'Case ID') === '' && get(row, 'Date') === '';
        if (isContinuation) {
            const procedureRaw = get(row, 'Procedure Type');
            // A continuation with no procedure is a regional block, which the ICM
            // catalogue has no equivalent for. Ignored rather than reported: it is
            // not a row the trainee lost, it is one this app has nowhere to put.
            if (procedureRaw === '' || parent === null)
                return;
            const key = procedureKeyFor(procedureRaw);
            if (key === null) {
                plan.skipped.push({
                    index,
                    reason: 'Procedure has no equivalent in this app',
                    value: procedureRaw,
                });
                return;
            }
            plan.procedures.push(procedure(key, parent.occurredAt, parent.ageBand, level(row, settings, 'Procedure Supervision'), {
                ...(variantFor(key, procedureRaw) === undefined
                    ? {}
                    : { variant: variantFor(key, procedureRaw) }),
            }));
            return;
        }
        const occurredAt = when(row, settings, 'Date', 'Time');
        if (occurredAt === null) {
            plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Date') });
            return;
        }
        const ageBand = age(row, settings);
        const capabilityLevel = level(row, settings);
        parent = { occurredAt, ageBand };
        const priorityRaw = normalise(get(row, 'Priority'));
        const priority = ANAESTHETIC_PRIORITIES.find((value) => value === priorityRaw);
        const asaRaw = Number(get(row, 'ASA').replace(/[^0-9]/g, ''));
        const asa = asaRaw >= 1 && asaRaw <= 5 ? asaRaw : undefined;
        const extras = ['Mode of Anaesthesia', 'Primary Specialty', 'Day Case', 'Significant Event'];
        const note = withNote(row, extras);
        plan.anaestheticCases.push({
            setting: 'theatre',
            title: get(row, 'Operation') || `Anaesthetic case — ${get(row, 'Date')}`,
            occurredAt,
            ageBand,
            capabilityLevel,
            ...(priority === undefined ? {} : { priority }),
            ...(asa === undefined ? {} : { asa }),
            ...(teaching(row) === undefined ? {} : { teachingGrades: teaching(row) }),
            ...(note === undefined ? {} : { note }),
        });
        // 239 of the 1,296 rows also record a procedure, with its own supervision.
        const procedureRaw = get(row, 'Procedure Type');
        if (procedureRaw === '')
            return;
        const key = procedureKeyFor(procedureRaw);
        if (key === null) {
            plan.skipped.push({
                index,
                reason: 'Procedure has no equivalent in this app',
                value: procedureRaw,
            });
            return;
        }
        plan.procedures.push(procedure(key, occurredAt, ageBand, level(row, settings, 'Procedure Supervision'), {
            ...(variantFor(key, procedureRaw) === undefined
                ? {}
                : { variant: variantFor(key, procedureRaw) }),
        }));
    });
    return plan;
}
/** Anaesthetics platform → acute pain reviews, as anaesthetic cases. */
function planLlpAcutePain(rows, settings) {
    const plan = { ...EMPTY_PLAN, anaestheticCases: [], skipped: [] };
    rows.forEach((row, index) => {
        const occurredAt = when(row, settings, 'Date', 'Time');
        if (occurredAt === null) {
            plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Date') });
            return;
        }
        const reviewType = get(row, 'Review Type');
        const title = reviewType === '' ? 'Acute pain review' : sentence(reviewType);
        const note = withNote(row, ['Pain Type', 'Review Nature', 'Role', 'Ward']);
        plan.anaestheticCases.push({
            setting: 'acute_pain',
            title,
            occurredAt,
            ageBand: age(row, settings),
            capabilityLevel: level(row, settings),
            ...(note === undefined ? {} : { note }),
        });
    });
    return plan;
}
/** Anaesthetics platform → sessions, as EDT entries. */
function planLlpSession(rows, settings) {
    const plan = { ...EMPTY_PLAN, edtEntries: [], skipped: [] };
    rows.forEach((row, index) => {
        const occurredAt = when(row, settings, 'Date', 'Time');
        if (occurredAt === null) {
            plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Date') });
            return;
        }
        const raw = get(row, 'Activity');
        const activity = edtActivityFor(raw);
        const note = withNote(row, ['Specialty (only if Activity=Theatre)']);
        plan.edtEntries.push({
            activity,
            title: raw === '' ? labelFor(EDT_ACTIVITY_OPTIONS, activity) : sentence(raw),
            occurredAt,
            ...(note === undefined ? {} : { note }),
        });
    });
    return plan;
}
/**
 * The platform's session vocabulary is not visible from one export, so anything
 * unrecognised becomes `other` rather than being dropped. A trainee can change
 * it afterwards; a skipped row they never see, they cannot.
 */
function edtActivityFor(value) {
    const raw = normalise(value);
    const map = {
        'pre operative assessments': 'clinic',
        clinic: 'clinic',
        clinics: 'clinic',
        teaching: 'teaching_delivered',
        'teaching delivered': 'teaching_delivered',
        'teaching attended': 'teaching_attended',
        'departmental teaching': 'teaching_attended',
        simulation: 'simulation',
        audit: 'project_audit',
        'quality improvement': 'project_audit',
        research: 'research',
        management: 'management',
        'exam preparation': 'exam_preparation',
        'self directed learning': 'self_directed_learning',
        course: 'course_conference',
        conference: 'course_conference',
    };
    return map[raw] ?? 'other';
}
/** `post-op-epidural-review` → `Post op epidural review`. */
function sentence(value) {
    const text = normalise(value);
    return (text.charAt(0).toUpperCase() + text.slice(1)).slice(0, 140);
}
/* ------------------------------------------------------ the Excel logbook */
/**
 * `ICU patients` — one row is an admission and up to a dozen procedures.
 *
 * This is the sheet that makes the whole importer worth building. Every
 * procedure has its own supervision column, so each one gets its own capability
 * level rather than inheriting the case's; that is what the columns mean and
 * pretending otherwise would flatten real information.
 */
function planExcelIcuPatients(rows, settings) {
    const plan = { ...EMPTY_PLAN, cases: [], skipped: [] };
    rows.forEach((row, index) => {
        const occurredAt = when(row, settings, 'Date');
        if (occurredAt === null) {
            const wrote = hasContent(row, [
                'Reason for admission\nPresenting complaint',
                'Reason for admission Presenting complaint',
                'Primary diagnosis',
                'Procedure 1',
                'Arterial line inserted',
                'CVC inserted',
                'Notes/Complications',
            ]);
            if (wrote)
                plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Date') });
            return;
        }
        const ageBand = age(row, settings);
        const caseLevel = level(row, settings);
        const reasons = [];
        if (isYes(row, 'Cardiac arrest'))
            reasons.push('cardiac_arrest');
        if (normalise(get(row, 'Outcome')) === 'organ donation')
            reasons.push('organ_donation');
        const hillos = [];
        if (isYes(row, 'Comms with relatives'))
            hillos.push(8);
        if (normalise(get(row, 'Outcome')) === 'organ donation')
            hillos.push(8);
        const title = get(row, 'Reason for admission\nPresenting complaint') ||
            get(row, 'Reason for admission Presenting complaint') ||
            get(row, 'Primary diagnosis') ||
            `Admission — ${get(row, 'Date')}`;
        /*
         * `Level of care` gets a field of its own as of 2026-08-30, so it leaves
         * the note — but only when it actually parses. An unrecognised value stays
         * in the note exactly as before, because the format of this column has
         * never been seen here (the real workbooks are on the human's machine) and
         * quietly dropping a trainee's own record would be the worse mistake.
         *
         * The organ-failure count column keeps going to the note: an organ count
         * was considered alongside level of care and deliberately not added.
         */
        const careLevel = parseCareLevel(get(row, 'Level of care'));
        const note = withNote(row, [
            'Primary diagnosis',
            'Primary specialty',
            ...(careLevel === null ? ['Level of care'] : []),
            'Number of organ failures (CVS, Resp, Renal, GI, Neuro, Haem)',
            'Involvement',
            'Outcome',
        ], 'Notes/Complications');
        const input = {
            // The sheet is admissions, but it has a column saying otherwise.
            caseType: isYes(row, 'Admitted') || get(row, 'Admitted') === '' ? 'admission' : 'ward_review',
            // `!== null`, not truthiness: level 0 is a real level of care.
            ...(careLevel === null ? {} : { careLevel }),
            title: title.slice(0, 140),
            occurredAt,
            ageBand,
            capabilityLevel: caseLevel,
            ...(reasons.length === 0 ? {} : { reasonsForReferral: reasons }),
            ...(hillos.length === 0 ? {} : { hillos: [...new Set(hillos)] }),
            ...(teaching(row) === undefined ? {} : { teachingGrades: teaching(row) }),
            ...(note === undefined ? {} : { note }),
        };
        const nested = [];
        const add = (key, columnLevel, extras = {}) => {
            nested.push(procedure(key, occurredAt, ageBand, level(row, settings, columnLevel), extras));
        };
        /* Arterial line, central lines and vascath each carry their own site. */
        const artery = get(row, 'Arterial line inserted');
        if (artery !== '' && normalise(artery) !== 'no') {
            add('arterial_line', 'Art Supervision', {
                ...(variantFor('arterial_line', artery) === undefined
                    ? {}
                    : { variant: variantFor('arterial_line', artery) }),
                ultrasoundGuided: isYes(row, 'Art USS'),
            });
        }
        for (const column of ['CVC inserted', '2nd line inserted', 'CVC 2 inserted']) {
            const site = get(row, column);
            if (site === '' || normalise(site) === 'no')
                continue;
            add('central_venous_catheter', 'CVC supervision', {
                ...(variantFor('central_venous_catheter', site) === undefined
                    ? {}
                    : { variant: variantFor('central_venous_catheter', site) }),
                ultrasoundGuided: isYes(row, 'CVC USS'),
            });
        }
        if (isYes(row, 'Vascath'))
            add('vascath', 'CVC supervision');
        /*
         * RSI and Intubation are separate yes/no columns describing one event. Both
         * yes is one intubation, not two. DAE then refines it: a fibreoptic makes
         * it a different procedure, a videolaryngoscope makes it a variant, and
         * everything else is a piece of kit this app does not model.
         */
        if (isYes(row, 'RSI') || isYes(row, 'Intubation')) {
            const dae = normalise(get(row, 'DAE'));
            if (dae.startsWith('fibreoptic')) {
                add('fibreoptic_intubation', 'Supervision');
            }
            else {
                add('intubation', 'Supervision', {
                    ...(dae === 'videolaryngoscope' ? { variant: 'Videolaryngoscopy' } : {}),
                    ...(dae === '' || dae === 'videolaryngoscope' ? {} : { note: `Airway adjunct: ${get(row, 'DAE')}` }),
                });
            }
        }
        for (const slot of [1, 2, 3, 4]) {
            const raw = get(row, `Procedure ${String(slot)}`);
            if (raw === '' || normalise(raw) === 'other')
                continue;
            const key = procedureKeyFor(raw);
            if (key === null) {
                plan.skipped.push({ index, reason: 'No matching procedure in this app', value: raw });
                continue;
            }
            add(key, `Procedure ${String(slot)} supervision`, {
                ...(variantFor(key, raw) === undefined ? {} : { variant: variantFor(key, raw) }),
            });
        }
        /* The ultrasound columns are resolved by their own table, not by name. */
        for (const slot of [1, 2]) {
            const raw = normalise(get(row, `Ultrasound ${String(slot)}`));
            if (raw === '')
                continue;
            const key = EXCEL_ULTRASOUND[raw];
            if (key === undefined || key === null) {
                plan.skipped.push({
                    index,
                    reason: 'No matching ultrasound in this app',
                    value: get(row, `Ultrasound ${String(slot)}`),
                });
                continue;
            }
            add(key, `USS ${String(slot)} Supervision`);
        }
        plan.cases.push({ input, procedures: nested });
    });
    return plan;
}
/** The Excel logbook's ward reviews. */
function planExcelWardReview(rows, settings) {
    const plan = { ...EMPTY_PLAN, cases: [], skipped: [] };
    const REASONS = {
        'airway concerns': 'airway_compromise',
        'resp support': 'respiratory_failure',
        'cvs support': 'haemodynamic_instability',
        'cardiac arrest': 'cardiac_arrest',
        'peri arrest': 'cardiac_arrest',
        'renal support': 'aki_rrt',
        trauma: 'major_trauma',
        'higher level obs': 'enhanced_monitoring',
        advice: 'other',
        'multi organ failure': 'other',
        'iv access': 'other',
        'other organ support': 'other',
        other: 'other',
    };
    rows.forEach((row, index) => {
        const occurredAt = when(row, settings, 'Date', 'Time of Day');
        if (occurredAt === null) {
            const wrote = hasContent(row, [
                'Primary Diagnosis',
                'Reason for consultation',
                'Intervention',
                'Notes',
            ]);
            if (wrote)
                plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Date') });
            return;
        }
        const reason = REASONS[normalise(get(row, 'Reason for consultation'))];
        const note = withNote(row, [
            'Reason for consultation',
            'Intervention',
            'Reason for non-admission',
            'Referring specialty',
        ]);
        plan.cases.push({
            input: {
                caseType: 'ward_review',
                title: (get(row, 'Primary Diagnosis') || `Ward review — ${get(row, 'Date')}`).slice(0, 140),
                occurredAt,
                ageBand: age(row, settings),
                capabilityLevel: level(row, settings),
                ...(reason === undefined ? {} : { reasonsForReferral: [reason] }),
                ...(teaching(row) === undefined ? {} : { teachingGrades: teaching(row) }),
                ...(note === undefined ? {} : { note }),
            },
            procedures: [],
        });
    });
    return plan;
}
/** The Excel logbook's transfers, which are procedures in this app. */
function planExcelTransfers(rows, settings) {
    const plan = { ...EMPTY_PLAN, procedures: [], skipped: [] };
    rows.forEach((row, index) => {
        const occurredAt = when(row, settings, 'Date', 'Time of Day');
        if (occurredAt === null) {
            const wrote = hasContent(row, ['Reason for transfer', 'Primary diagnosis', 'Notes']);
            if (wrote)
                plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Date') });
            return;
        }
        const key = normalise(get(row, 'Intra/Extra hospital')).startsWith('extra')
            ? 'transfer_inter_hospital'
            : 'transfer_intra_hospital';
        const note = withNote(row, [
            'Reason for transfer',
            'Primary diagnosis',
            'Transport',
            'Artificial ventilation',
        ]);
        plan.procedures.push(procedure(key, occurredAt, age(row, settings), level(row, settings), {
            ...(note === undefined ? {} : { note }),
        }));
    });
    return plan;
}
/* --------------------------------------------------------- IMT logbook */
/**
 * `Hours` on the two Teaching sheets: a plain number of hours, not minutes.
 * `0` and blank both mean "not recorded" — the schema requires a duration of
 * at least a minute, and a false `0` would read as a session that took no
 * time at all rather than one nobody timed.
 */
function hoursDuration(row, header = 'Hours') {
    const raw = get(row, header).replace(',', '.');
    if (raw === '')
        return undefined;
    const hours = Number(raw);
    if (!Number.isFinite(hours) || hours <= 0)
        return undefined;
    const minutes = Math.round(hours * 60);
    return Math.min(Math.max(minutes, 1), MAX_EDT_DURATION_MINUTES);
}
/** IMT logbook `Teaching Record` → EDT entries, teaching attended. */
function planImtTeachingAttended(rows, settings) {
    const plan = { ...EMPTY_PLAN, edtEntries: [], skipped: [] };
    rows.forEach((row, index) => {
        const occurredAt = when(row, settings, 'Date');
        if (occurredAt === null) {
            plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Date') });
            return;
        }
        const topic = get(row, 'Topic');
        const note = withNote(row, ['Teaching Type']);
        const duration = hoursDuration(row);
        plan.edtEntries.push({
            activity: 'teaching_attended',
            title: (topic || labelFor(EDT_ACTIVITY_OPTIONS, 'teaching_attended')).slice(0, 140),
            occurredAt,
            ...(duration === undefined ? {} : { durationMinutes: duration }),
            ...(note === undefined ? {} : { note }),
        });
    });
    return plan;
}
/** IMT logbook `Teaching Delivered` → EDT entries, teaching delivered. */
function planImtTeachingDelivered(rows, settings) {
    const plan = { ...EMPTY_PLAN, edtEntries: [], skipped: [] };
    rows.forEach((row, index) => {
        const occurredAt = when(row, settings, 'Date');
        if (occurredAt === null) {
            plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Date') });
            return;
        }
        const topic = get(row, 'Topic');
        const note = withNote(row, ['Teaching Type', 'Audience', 'Feedback']);
        plan.edtEntries.push({
            activity: 'teaching_delivered',
            title: (topic || labelFor(EDT_ACTIVITY_OPTIONS, 'teaching_delivered')).slice(0, 140),
            occurredAt,
            ...(note === undefined ? {} : { note }),
        });
    });
    return plan;
}
/** IMT logbook `Clinic Record` → EDT entries, clinic attended. */
function planImtClinic(rows, settings) {
    const plan = { ...EMPTY_PLAN, edtEntries: [], skipped: [] };
    rows.forEach((row, index) => {
        const occurredAt = when(row, settings, 'Clinic Date');
        if (occurredAt === null) {
            plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Clinic Date') });
            return;
        }
        const diagnosis = get(row, 'Diagnosis');
        const clinicType = get(row, 'Clinic Type');
        const note = withNote(row, ['Clinic Type']);
        plan.edtEntries.push({
            activity: 'clinic',
            title: (diagnosis ||
                clinicType ||
                `${labelFor(EDT_ACTIVITY_OPTIONS, 'clinic')} — ${get(row, 'Clinic Date')}`).slice(0, 140),
            occurredAt,
            ...(note === undefined ? {} : { note }),
        });
    });
    return plan;
}
/**
 * IMT logbook `Procedures` → stand-alone procedures.
 *
 * No age band or capability level column exists at all, so every row takes
 * the wizard's default for both — the same choice `LOGBOOK_STAND_ALONE_PROCEDURE`
 * made when it lost only the age column. The sheet also ships with a data
 * validation dropdown list pasted into its own rows below the real table
 * (`List of procedures`, `Abdominal paracentesis`, …); those carry a
 * `Procedure` value but nothing else and are not a trainee's data, so they are
 * ignored rather than reported as rows with no usable date.
 */
function planImtProcedures(rows, settings) {
    const plan = { ...EMPTY_PLAN, procedures: [], skipped: [] };
    rows.forEach((row, index) => {
        const occurredAt = when(row, settings, 'Date');
        if (occurredAt === null) {
            const wrote = hasContent(row, ['Skills lab', 'Supervised practice', 'DOPS completed', 'Comments']);
            if (wrote)
                plan.skipped.push({ index, reason: 'No usable date', value: get(row, 'Date') });
            return;
        }
        const raw = get(row, 'Procedure');
        if (raw === '') {
            plan.skipped.push({ index, reason: 'No procedure named', value: '' });
            return;
        }
        const key = procedureKeyFor(raw);
        if (key === null) {
            plan.skipped.push({ index, reason: 'No matching procedure in this app', value: raw });
            return;
        }
        const note = withNote(row, ['Skills lab', 'Supervised practice', 'DOPS completed'], 'Comments');
        plan.procedures.push(procedure(key, occurredAt, age(row, settings), level(row, settings), {
            ...(variantFor(key, raw) === undefined ? {} : { variant: variantFor(key, raw) }),
            ...(note === undefined ? {} : { note }),
        }));
    });
    return plan;
}
/* ------------------------------------------------------------ dispatch */
const PLANNERS = {
    llp_intensive: planLlpIntensive,
    llp_procedures: planLlpProcedures,
    llp_anaesthetic: planLlpAnaesthetic,
    llp_acute_pain: planLlpAcutePain,
    llp_session: planLlpSession,
    excel_icu_patients: planExcelIcuPatients,
    excel_ward_review: planExcelWardReview,
    excel_transfers: planExcelTransfers,
    imt_teaching_attended: planImtTeachingAttended,
    imt_teaching_delivered: planImtTeachingDelivered,
    imt_clinic: planImtClinic,
    imt_procedures: planImtProcedures,
};
/**
 * What importing this sheet would do, worked out before anything is written.
 *
 * Nothing here touches a database. The caller shows the plan, the user confirms
 * it, and only then does a repository write — which is the same shape the
 * restore path uses, and for the same reason.
 */
export function planProfileImport(profile, rows, settings) {
    const planner = PLANNERS[profile.key];
    if (planner === undefined)
        return EMPTY_PLAN;
    const plan = planner(rows, settings);
    const headers = rows[0] === undefined ? [] : Object.keys(rows[0]);
    return { ...plan, refused: refusedColumns(headers) };
}
/** Everything the plan would create, for the confirmation line in the wizard. */
export function planTotals(plan) {
    const nested = plan.cases.reduce((sum, entry) => sum + entry.procedures.length, 0);
    const procedures = plan.procedures.length + nested;
    return {
        cases: plan.cases.length,
        procedures,
        edtEntries: plan.edtEntries.length,
        anaestheticCases: plan.anaestheticCases.length,
        total: plan.cases.length + procedures + plan.edtEntries.length + plan.anaestheticCases.length,
    };
}
//# sourceMappingURL=importProfiles.js.map